import { Undo2 } from 'lucide-react'

import type { DataReviewRefusal } from '@/services/dataReview'

/**
 * The three verbs, for a row whose card is not a checklist item: Confirm
 * (keep what is stored), Correct (a value a person types or picks) and Not
 * available (an honest absence, with its reason).
 *
 * Each button answers THIS row and only this row — there is deliberately no
 * control anywhere that answers several. A verb a row does not offer is not
 * drawn, so a row with no value never shows a Confirm.
 */
export function DecisionBar({
  busy,
  confirm,
  correct,
  unavailable,
}: {
  busy: boolean
  /** Present only when the row offers Confirm. */
  confirm?: { label: string; title?: string; primary?: boolean; onClick: () => void }
  /** Present only when the row offers Correct. */
  correct?: {
    value: string
    onValue: (value: string) => void
    placeholder: string
    /** A controlled vocabulary for the field, when it has one. */
    options?: string[]
    onUse: (value: string) => void
  }
  /** Present only when the row offers Not available. */
  unavailable?: {
    reason: string
    onReason: (reason: string) => void
    onUse: (reason: string) => void
  }
}) {
  return (
    <div className="flex flex-wrap items-center gap-1.5">
      {confirm && (
        <button
          type="button"
          disabled={busy}
          onClick={confirm.onClick}
          title={confirm.title}
          className={`${confirm.primary ? 'btn-primary' : 'btn-secondary'} px-3 py-1.5 text-[11px] disabled:opacity-40`}
        >
          {busy ? 'Saving…' : confirm.label}
        </button>
      )}
      {correct && (
        <>
          {correct.options ? (
            <select
              value={correct.value}
              onChange={(e) => correct.onValue(e.target.value)}
              aria-label={correct.placeholder}
              className="input w-[200px] text-[11px]"
            >
              <option value="">— choose a value —</option>
              {correct.options.map((o) => <option key={o} value={o}>{o}</option>)}
            </select>
          ) : (
            <input
              value={correct.value}
              onChange={(e) => correct.onValue(e.target.value)}
              placeholder={correct.placeholder}
              aria-label={correct.placeholder}
              className="input w-[220px] text-[11px]"
            />
          )}
          <button
            type="button"
            disabled={busy || !correct.value.trim()}
            onClick={() => correct.onUse(correct.value.trim())}
            className="btn-secondary px-3 py-1.5 text-[11px] disabled:opacity-40"
          >
            Use this value
          </button>
        </>
      )}
      {unavailable && (
        <div className="ml-auto flex items-center gap-1.5">
          <input
            value={unavailable.reason}
            onChange={(e) => unavailable.onReason(e.target.value)}
            placeholder="Why is it unavailable?"
            aria-label="Why is it unavailable?"
            className="input w-[190px] text-[10.5px]"
          />
          <button
            type="button"
            disabled={busy || !unavailable.reason.trim()}
            onClick={() => unavailable.onUse(unavailable.reason.trim())}
            className="rounded border border-stone-700 px-2 py-1.5 text-[10.5px] text-stone-400 transition hover:border-stone-500 hover:text-stone-200 disabled:opacity-40"
          >
            Not available
          </button>
        </div>
      )}
    </div>
  )
}

/**
 * A person already decided this, and the screen will not overwrite them
 * quietly. The refusal says whose answer it is and when; the reviewer either
 * withdraws it (the engine's reading is back) or overrules it — and the
 * overrule is recorded against their own name.
 */
export function OverridePrompt({
  refusal,
  busy,
  onWithdraw,
  onOverrule,
  onLeave,
}: {
  refusal: DataReviewRefusal
  busy: boolean
  onWithdraw: () => void
  onOverrule: () => void
  onLeave: () => void
}) {
  return (
    <div role="alert" className="rounded border border-amber-400/30 bg-amber-400/[0.07] px-3 py-2">
      <p className="text-[11px] leading-relaxed text-amber-100">{refusal.message}</p>
      <div className="mt-2 flex flex-wrap items-center gap-1.5">
        {refusal.withdrawable && (
          <button
            type="button"
            disabled={busy}
            onClick={onWithdraw}
            className="inline-flex items-center gap-1 rounded border border-stone-600 px-2 py-1 text-[10.5px] text-stone-200 transition hover:border-amber-400/50 hover:text-amber-200 disabled:opacity-40"
          >
            <Undo2 size={10} /> Withdraw their decision
          </button>
        )}
        <button
          type="button"
          disabled={busy}
          onClick={onOverrule}
          className="rounded border border-amber-400/40 bg-amber-400/15 px-2 py-1 text-[10.5px] font-medium text-amber-100 transition hover:bg-amber-400/25 disabled:opacity-40"
        >
          Overrule it — recorded against my name
        </button>
        <button
          type="button"
          onClick={onLeave}
          className="px-2 py-1 text-[10.5px] text-stone-400 hover:text-stone-200"
        >
          Leave it as it is
        </button>
      </div>
    </div>
  )
}
